"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import { useMemo } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { orderNavItemsByMenuSort } from "@flowx/shell-ui/navbar";

const NeuralParticle = dynamic(
  () => import("@/components/particle/NeuralParticle"),
  { ssr: false },
);

// 落地页导航。静态顺序对齐超管端权限管理的模块 sort（未登录回退）；
// 登录后按 session.menus 下发的 level=1 模块顺序动态排序。
const staticNavItems = [
  { name: "AI媒体流", path: "/media-flow", module: "media" },
  { name: "电商生图", path: "/ecom-image", module: "ecom-image" },
  { name: "商智引擎", path: "/strategy", module: "strategy" },
  { name: "AI开品", path: "/ai-product", module: "product" },
  { name: "小万同学", path: "/xiaowan", module: "xiaowan" },
];

export default function Home() {
  // 首页不做登录态跳转:登录用户停留在落地页,由导航栏自行进入各模块;
  // 模块内部的重定向(如 /strategy/ → ManagementDashboard)由各模块路由自己负责。
  const { permissionMenus } = useAuth();
  const navItems = useMemo(
    () => orderNavItemsByMenuSort(staticNavItems, permissionMenus),
    [permissionMenus],
  );
  return (
    <div className="fixed inset-0 z-300 overflow-hidden bg-black">
      {/* Particle Background */}
      <div className="absolute inset-0">
        <NeuralParticle
          config={{ uploadedImage: "/particle-source-landing.webp" }}
        />
      </div>

      {/* Dark Overlay */}
      <motion.div
        className="absolute inset-0 bg-black"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.6 }}
        transition={{ delay: 2, duration: 1.2, ease: "easeInOut" }}
      />

      {/* Top Navigation - Pill Menu */}
      <motion.nav
        className="absolute top-6 left-0 right-0 z-10 flex items-center justify-center px-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.5, duration: 0.8, ease: "easeOut" }}
      >
        <Link href="/" className="absolute left-16 top-1/2 -translate-y-1/2">
          <img src="/logo.png" alt="FlowX AI" className="h-8 w-auto" />
        </Link>
        <div className="inline-flex items-center gap-1 px-20 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 whitespace-nowrap">
          {navItems.map((item) => (
            <Link
              key={item.path}
              href={item.path}
              className="px-5 py-2 text-[14px] font-medium text-white/85 hover:text-white rounded-full hover:bg-white/10 transition-all duration-200"
            >
              {item.name}
            </Link>
          ))}
        </div>
      </motion.nav>

      {/* Center Content */}
      <div className="absolute inset-0 flex items-end mb-48 z-10">
        <div className="ml-[64px] max-w-2xl">
          <motion.h1
            className="text-[48px] font-bold text-white leading-tight tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3, duration: 0.8, ease: "easeOut" }}
          >
            欢迎使用 FlowX Pro
          </motion.h1>

          <motion.p
            className="mt-5 text-[16px] leading-relaxed text-white/70 max-w-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.3, duration: 0.8, ease: "easeOut" }}
          >
            一站式AI智能化综合平台，为企业和个人用户提供智能体对话、
            <br />
            商业分析、AI生图等多元化AI服务
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.6, duration: 0.8, ease: "easeOut" }}
          >
            <Link
              href="/login"
              className="group relative inline-flex items-center justify-center mt-8"
            >
              <img
                src="/login-btn.png"
                alt=""
                className="h-[52px] w-auto transition-opacity duration-200 group-hover:opacity-0"
              />
              <img
                src="/login-btn-active.png"
                alt=""
                className="absolute inset-0 h-full w-full object-contain opacity-0 transition-opacity duration-200 group-hover:opacity-100"
              />
              <span className="absolute inset-0 flex items-center justify-center text-white text-[20px] font-medium">
                开始体验
              </span>
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
